import { useAuthenticationStore } from '@/stores/AuthenticationStore/AuthenticationStore';
import { useInventoryStore } from '@/stores/InventoryStore/InventoryStore';
import { cdn } from '@/utils/cdn';
import { handleHttpError } from '@/utils/errorHandlers';
import { getArtworkLabels } from '@/views/gallery/artworks/artwork-utils';
import {
  type ArcualMediaItem,
  type ArcualInventoryItem,
  type ArcualSalesAgreement,
  type ArcualSalesAgreementProduct,
  type ArcualTransfer,
  ArcualTransferRole,
  type ArcualTransfersListResponse,
  ArcualUserContext,
  CheckoutEventMessage,
  getSalesAgreementService,
} from '@art-basel/artbasel-lib-platform';
import type { EventSourceMessage } from '@microsoft/fetch-event-source';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useAccountStore } from '../AccountStore/AccountStore';

const SalesAgreementStoreName = 'SalesAgreementStore';

export const useSalesAgreementStore = defineStore(SalesAgreementStoreName, () => {
  const salesAgreementsList = ref<ArcualSalesAgreement[]>();
  const transfersList = ref<ArcualTransfer[]>();
  let sseCallbacks: Array<{
    key: string;
    salesAgreementId: string;
    callback: (event: CheckoutEventMessage, salesAgreement: string) => void;
  }> = [];
  let sseInitialized = false;

  const getSalesAgreementById = async (id: string, populateSalesAgreement = false): Promise<ArcualSalesAgreement> => {
    try {
      const isGuest = !useAuthenticationStore().isAuthenticated().value;
      let agreement: ArcualSalesAgreement = await getSalesAgreementService().getSalesAgreementById(id, isGuest);

      if (populateSalesAgreement) {
        agreement = await useSalesAgreementStore().populateSalesAgreement<ArcualSalesAgreement>(agreement);
      }

      return agreement;
    } catch (error) {
      handleHttpError(error);
      return null as unknown as ArcualSalesAgreement;
    }
  };

  const getTransferById = async (id: string, populateTransfer = false): Promise<ArcualTransfer> => {
    try {
      const isGuest = !useAuthenticationStore().isAuthenticated().value;
      let transfer: ArcualTransfer = await getSalesAgreementService().getTransferById(id, isGuest);
      if (populateTransfer) {
        transfer = await useSalesAgreementStore().populateTransfer<ArcualTransfer>(transfer);
      }
      return transfer;
    } catch (error) {
      handleHttpError(error);
      return null as unknown as ArcualTransfer;
    }
  };

  const getSalesAgreementStatusById = async (id: string): Promise<Partial<ArcualSalesAgreement>> => {
    try {
      return await getSalesAgreementService().getSalesAgreementStatusById(id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const getTransferStatusById = async (id: string): Promise<Partial<ArcualTransfer>> => {
    try {
      return await getSalesAgreementService().getSalesAgreementStatusById(id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  type PopulateSalesAgreementType = Partial<ArcualSalesAgreement> | ArcualSalesAgreement; // workaround for TS so we can use extends

  const populateSalesAgreement = async <T extends PopulateSalesAgreementType>(salesAgreement: T): Promise<T> => {
    const agreement = { ...salesAgreement };
    if (agreement.buyerProfileId && useAuthenticationStore().isAuthenticated().value) {
      try {
        agreement.buyer = await useAccountStore().getAccountById(agreement.buyerProfileId);
      } catch (_) {
        agreement.buyer = undefined;
      }
    }
    if (agreement.sellerProfileId && agreement.id) {
      // we use different endpoints based on auth status
      try {
        if (useAuthenticationStore().isAuthenticated().value) {
          agreement.seller = await useAccountStore().getAccountById(agreement.sellerProfileId);
        } else {
          agreement.seller = {
            ...(await getSalesAgreementService().getSalesAgreementSeller(agreement.id)),
            privacyPolicyAccepted: true,
            termsConditionsAccepted: true,
          };
        }
      } catch (_) {
        agreement.seller = undefined;
      }
    }
    if (agreement.artworks) {
      for (const index in agreement.artworks) {
        try {
          const artwork = agreement.artworks[index];
          agreement.artworks[index] = await getSalesAgreementArtwork(artwork);
        } catch (_) {
          //
        }
      }
    }
    return agreement;
  };

  type PopulateTransferType = Partial<ArcualTransfer> | ArcualTransfer; // workaround for TS so we can use extends

  const populateTransfer = async <T extends PopulateTransferType>(transfer: T): Promise<T> => {
    const populatedTransfer = { ...transfer };
    if (populatedTransfer.buyerPlatformAccountId && useAuthenticationStore().isAuthenticated().value) {
      try {
        populatedTransfer.buyer = await useAccountStore().getAccountById(populatedTransfer.buyerPlatformAccountId);
      } catch (_) {
        populatedTransfer.buyer = undefined;
      }
    }
    if (populatedTransfer.sellerPlatformAccountId && populatedTransfer.id) {
      // we use different endpoints based on auth status
      try {
        if (useAuthenticationStore().isAuthenticated().value) {
          populatedTransfer.seller = await useAccountStore().getAccountById(populatedTransfer.sellerPlatformAccountId);
        } else {
          populatedTransfer.seller = {
            ...(await getSalesAgreementService().getSalesAgreementSeller(populatedTransfer.id)),
            privacyPolicyAccepted: true,
            termsConditionsAccepted: true,
          };
        }
      } catch (_) {
        populatedTransfer.seller = undefined;
      }
    }
    if (populatedTransfer.items) {
      for (const index in populatedTransfer.items) {
        try {
          const artwork = populatedTransfer.items[index];
          populatedTransfer.items[index] = await getSalesAgreementArtwork(artwork);
        } catch (_) {
          continue;
        }
      }
    }
    return populatedTransfer;
  };

  const saveSalesAgreement = async (salesAgreement: Partial<ArcualSalesAgreement>) => {
    try {
      if (typeof salesAgreement.id !== 'undefined' && salesAgreement.id.length > 0) {
        return await getSalesAgreementService().updateSalesAgreement(salesAgreement);
      } else {
        return await getSalesAgreementService().createSalesAgreement(salesAgreement);
      }
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const saveTransfer = async (transfer: Partial<ArcualTransfer>) => {
    try {
      if (typeof transfer.id !== 'undefined' && transfer.id.length > 0) {
        return await getSalesAgreementService().updateTransfer(transfer);
      } else {
        return await getSalesAgreementService().createTransfer(transfer);
      }
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const sendSalesAgreement = async (salesAgreement: ArcualSalesAgreement) => {
    try {
      const salesAgreementStore = useSalesAgreementStore();
      const saved = await salesAgreementStore.saveSalesAgreement(salesAgreement);
      await getSalesAgreementService().sendSalesAgreement(saved.id);
      return await salesAgreementStore.getSalesAgreementById(saved.id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const sendTransfer = async (transfer: Partial<ArcualTransfer>) => {
    try {
      const salesAgreementStore = useSalesAgreementStore();
      const saved = await salesAgreementStore.saveTransfer(transfer);
      await getSalesAgreementService().sendTransfer(saved.id);
      return await salesAgreementStore.getTransferById(saved.id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const acceptTransfer = async (transfer: ArcualTransfer): Promise<ArcualTransfer> => {
    try {
      await getSalesAgreementService().acceptTransfer(transfer.id);
      return await useSalesAgreementStore().getTransferById(transfer.id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const withdrawSalesAgreement = async (salesAgreement: ArcualSalesAgreement) => {
    try {
      await getSalesAgreementService().withdrawSalesAgreement(salesAgreement.id);
      return await useSalesAgreementStore().getSalesAgreementById(salesAgreement.id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const withdrawTransfer = async (transfer: ArcualTransfer) => {
    try {
      await getSalesAgreementService().withdrawTransfer(transfer.id);
      return await useSalesAgreementStore().getTransferById(transfer.id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const deleteSalesAgreement = async (salesAgreement: ArcualSalesAgreement): Promise<void> => {
    try {
      await getSalesAgreementService().deleteSalesAgreement(salesAgreement.id as string);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const getSalesAgreements = async (
    context: ArcualUserContext = ArcualUserContext.Seller,
  ): Promise<ArcualSalesAgreement[]> => {
    try {
      salesAgreementsList.value = await getSalesAgreementService().getSalesAgreements(context);
      return salesAgreementsList.value || [];
    } catch (error) {
      handleHttpError(error);
      return null as unknown as ArcualSalesAgreement[];
    }
  };

  const getTransfers = async (
    role: ArcualTransferRole = ArcualTransferRole.SELLER,
    page: number = 1,
  ): Promise<ArcualTransfersListResponse> => {
    try {
      const response = await getSalesAgreementService().getTransfers(role, page);
      transfersList.value = response.items;
      return response;
    } catch (error) {
      handleHttpError(error);
      return null as unknown as ArcualTransfersListResponse;
    }
  };

  const getSalesAgreementArtwork = async (
    salesAgreementArtwork: ArcualSalesAgreementProduct,
  ): Promise<ArcualSalesAgreementProduct> => {
    const artwork = (await useInventoryStore().getItem(salesAgreementArtwork.id, { invalidateCache: true })).value;

    return {
      ...salesAgreementArtwork,
      discountAmount: salesAgreementArtwork.discountAmount || '0',
      listPrice: salesAgreementArtwork.listPrice ?? artwork.listPrice?.amount.toString(),
      artwork: {
        ...artwork,
        labels: getArtworkLabels(artwork),
        images:
          artwork.images.map((image: ArcualMediaItem) => ({
            ...image,
            id: cdn(image.id),
            url: image.url,
          })) || [],
      } as ArcualInventoryItem,
    };
  };

  const sseEventHandler = async (sseEventPayload: EventSourceMessage) => {
    console.debug('salesAgreementSseConnection', sseEventPayload);

    const event = sseEventPayload.event;
    const { salesAgreementId } = JSON.parse(sseEventPayload.data);

    switch (event) {
      case CheckoutEventMessage.ACCEPTED:
      case CheckoutEventMessage.REJECTED:
        console.debug(`CHECKOUT EVENT -  ${salesAgreementId}`, event);

        sseCallbacks
          .filter((listener) => listener.salesAgreementId === salesAgreementId)
          .forEach((listener) => listener.callback(event, salesAgreementId));
        break;
      case CheckoutEventMessage.CREATED:
      case CheckoutEventMessage.CANCELLED:
      default:
        break;
    }
  };

  const initSseConnection = async () => {
    await getSalesAgreementService().salesAgreementSseConnection(sseEventHandler);
  };

  const addSseListener = async (
    key: string,
    salesAgreementId: string,
    callback: (event: CheckoutEventMessage, salesAgreement: string) => void,
  ) => {
    sseCallbacks.push({ key, salesAgreementId, callback });

    if (!sseInitialized) {
      await initSseConnection();
      sseInitialized = true;
    }
  };

  const removeSseListener = async (key: string, salesAgreementId: string) => {
    sseCallbacks = sseCallbacks.filter(
      (listener) => !(listener.key === key && listener.salesAgreementId === salesAgreementId),
    );
  };

  const reset = () => {
    salesAgreementsList.value = undefined;
  };

  return {
    salesAgreementsList,
    transfersList,
    getSalesAgreements,
    getSalesAgreementById,
    getSalesAgreementStatusById,
    getTransferStatusById,
    populateSalesAgreement,
    saveSalesAgreement,
    getTransfers,
    getTransferById,
    populateTransfer,
    saveTransfer,
    sendSalesAgreement,
    sendTransfer,
    acceptTransfer,
    withdrawSalesAgreement,
    withdrawTransfer,
    deleteSalesAgreement,
    addSseListener,
    removeSseListener,
    reset,
  };
});
